import { useEffect, useState, useCallback } from "react";
import { crmApi } from "@/lib/crm";
import { Card, Stat, Btn, Input, SectionTitle, Empty, Table, Pill } from "./kit";

export function AiCommandTab({ setTab }) {
  const [queue, setQueue] = useState(null);
  useEffect(() => { crmApi("/ai-command").then(setQueue).catch(() => setQueue([])); }, []);
  if (!queue) return <Empty>Loading command queue…</Empty>;
  return (
    <Card data-testid="crm-ai-command">
      <SectionTitle>AI Command — action queue</SectionTitle>
      {queue.length === 0 ? <Empty>All clear — nothing needs attention</Empty> : (
        <div className="space-y-3">
          {queue.map((q) => (
            <div key={q.id} className="flex items-center gap-4 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5">
              <Pill tone={q.priority === "high" ? "red" : q.priority === "medium" ? "amber" : "slate"}>{q.priority}</Pill>
              <div className="flex-1">
                <div className="text-sm font-semibold">{q.title}</div>
                <div className="text-xs text-slate-500">{q.detail}</div>
              </div>
              <Btn onClick={() => setTab(q.tab)} data-testid={`ai-command-open-${q.area}`}>Open</Btn>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

const REC_STATUSES = ["recommended", "approved", "done", "dismissed"];

export function SeoTab() {
  const [overview, setOverview] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [recs, setRecs] = useState([]);
  const [ideas, setIdeas] = useState(0);
  const [kw, setKw] = useState("");
  const [compName, setCompName] = useState("");
  const [compUrl, setCompUrl] = useState("");

  const load = useCallback(() => {
    crmApi("/seo/overview").then(setOverview).catch(() => {});
    crmApi("/seo/keywords").then(setKeywords).catch(() => {});
    crmApi("/seo/competitors").then(setCompetitors).catch(() => {});
    crmApi("/seo/recommendations").then(setRecs).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const addKeyword = async (e) => { e.preventDefault(); if (!kw.trim()) return; await crmApi("/seo/keywords", { method: "POST", body: { keyword: kw } }); setKw(""); load(); };
  const addCompetitor = async (e) => { e.preventDefault(); if (!compName.trim() || !compUrl.trim()) return; await crmApi("/seo/competitors", { method: "POST", body: { name: compName, url: compUrl } }); setCompName(""); setCompUrl(""); load(); };
  const setRecStatus = async (id, status) => { await crmApi(`/seo/recommendations/${id}`, { method: "PATCH", body: { status } }); load(); };
  const contentPlan = async () => { const r = await crmApi("/seo/content-plan", { method: "POST" }); setIdeas(r.added); load(); };

  if (!overview) return <Empty>Loading SEO Lab…</Empty>;
  return (
    <div className="space-y-6" data-testid="crm-seo">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Keywords tracked" value={overview.keywords} />
        <Stat label="Pending recommendations" value={overview.recommendations_pending} />
        <Stat label="Competitors watched" value={overview.competitors} />
        <Stat label="Content ideas" value={overview.content_ideas} />
      </div>
      <Card>
        <SectionTitle>Connectors</SectionTitle>
        <div className="flex flex-wrap gap-3 mb-4">
          <Pill tone="amber">Google Search Console: {overview.connectors.google_search_console.replace("_", " ")}</Pill>
          <Pill tone="amber">Rank provider: {overview.connectors.rank_provider.replace("_", " ")}</Pill>
        </div>
        <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 text-xs text-slate-400 leading-relaxed" data-testid="gsc-connector-panel">
          <span className="text-slate-200 font-semibold">To link Google Search Console:</span> once noxway.shop is live,
          verify the domain at search.google.com/search-console (HTML tag or DNS method), then tell your developer
          (or ask in chat) to connect the Search Console API with your Google account. Real impressions, clicks, CTR
          and average position will populate here — nothing is shown until the real connection exists.
        </div>
        <p className="text-xs text-slate-500 mt-3">Planning scores only — never fabricated metrics.</p>
      </Card>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <SectionTitle>Keywords</SectionTitle>
          <form onSubmit={addKeyword} className="flex gap-2.5 mb-5">
            <Input value={kw} onChange={(e) => setKw(e.target.value)} placeholder="wildfire protection gel" data-testid="seo-keyword-input" />
            <Btn type="submit" data-testid="seo-keyword-add">Add</Btn>
          </form>
          {keywords.length === 0 ? <Empty>No keywords yet</Empty> : (
            <ul className="space-y-2">
              {keywords.map((k) => (
                <li key={k.id} className="flex items-center gap-3 text-sm bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5">
                  <span className="flex-1">{k.keyword}</span>
                  <Pill>{k.intent}</Pill>
                  <span className="text-[10px] text-slate-600">{k.note}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card>
          <SectionTitle>Competitor watch</SectionTitle>
          <form onSubmit={addCompetitor} className="flex gap-2.5 mb-5">
            <Input value={compName} onChange={(e) => setCompName(e.target.value)} placeholder="Name" data-testid="seo-comp-name" />
            <Input value={compUrl} onChange={(e) => setCompUrl(e.target.value)} placeholder="https://…" data-testid="seo-comp-url" />
            <Btn type="submit" data-testid="seo-comp-add">Add</Btn>
          </form>
          {competitors.length === 0 ? <Empty>No competitors watched</Empty> : (
            <ul className="space-y-2">
              {competitors.map((c) => (
                <li key={c.id} className="text-sm bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5">
                  <span className="font-semibold">{c.name}</span>
                  <span className="text-xs text-slate-500 ml-2 break-all">{c.url}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
      <Card>
        <SectionTitle>Recommendations</SectionTitle>
        {recs.length === 0 ? <Empty>No recommendations yet — new ones appear from verified data once connectors are linked</Empty> : (
          <div className="space-y-3">
            {recs.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-3 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{r.title}</div>
                  {r.detail && <div className="text-xs text-slate-500">{r.detail}</div>}
                </div>
                <Pill tone={r.status === "approved" ? "green" : r.status === "dismissed" ? "red" : r.status === "done" ? "slate" : "amber"}>{r.status}</Pill>
                <select value={r.status} onChange={(e) => setRecStatus(r.id, e.target.value)} data-testid="seo-rec-status"
                  className="bg-white/5 border border-white/15 rounded-lg px-2 py-1.5 text-xs outline-none">
                  {REC_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card>
        <SectionTitle>Content opportunities</SectionTitle>
        <Btn onClick={contentPlan} data-testid="seo-content-plan">Generate content plan</Btn>
        {ideas > 0 && <span className="text-sm text-[#f97316] ml-4" data-testid="seo-plan-result">{ideas} new idea(s) added — drafts only, nothing published</span>}
      </Card>
      <BlogManager />
    </div>
  );
}

function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const load = useCallback(() => crmApi("/blog").then(setPosts).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const generate = async (e) => {
    e.preventDefault();
    if (!title.trim() || busy) return;
    setBusy(true);
    setNote("AI is writing the draft — takes a few seconds…");
    try {
      await crmApi("/blog/generate", { method: "POST", body: { title } });
      setTitle("");
      setNote("Draft ready — review it below, then publish when happy");
      load();
    } catch (err) { setNote(err.response?.data?.detail || "Generation failed"); }
    setBusy(false);
  };
  const setStatus = async (id, status) => { await crmApi(`/blog/${id}`, { method: "PATCH", body: { status } }); load(); };
  return (
    <Card data-testid="blog-manager">
      <SectionTitle>Blog engine</SectionTitle>
      <form onSubmit={generate} className="flex gap-2.5 mb-5">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Article title or idea, e.g. Evacuation checklist: the 30-minute property prep" data-testid="blog-title-input" />
        <Btn type="submit" disabled={busy} data-testid="blog-generate">{busy ? "Writing…" : "Write draft"}</Btn>
      </form>
      {note && <p className="text-sm text-[#f97316] mb-4" data-testid="blog-note">{note}</p>}
      {posts.length === 0 ? <Empty>No posts yet — drafts appear at /blog once published</Empty> : (
        <ul className="space-y-3">
          {posts.map((p) => (
            <li key={p.id} className="bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">{p.title}</div>
                <div className="text-xs text-slate-500 truncate">{p.excerpt}</div>
              </div>
              {p.ai && <Pill tone="orange">AI draft</Pill>}
              <Pill tone={p.status === "published" ? "green" : "amber"}>{p.status}</Pill>
              {p.status === "published" ? (
                <>
                  <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="font-tech text-[10px] tracking-wider text-[#f97316] hover:underline uppercase">View</a>
                  <Btn onClick={() => setStatus(p.id, "draft")} data-testid="blog-unpublish">Unpublish</Btn>
                </>
              ) : (
                <Btn onClick={() => setStatus(p.id, "published")} data-testid="blog-publish">Publish</Btn>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-slate-600 mt-4">Publishing never happens automatically — every post goes live only when you hit Publish.</p>
    </Card>
  );
}

const GROWTH_STATUSES = ["recommended", "approved", "in_progress", "done", "dismissed"];

export function AiGrowthTab() {
  const [data, setData] = useState(null);
  const [noteUrl, setNoteUrl] = useState("");
  const [noteText, setNoteText] = useState("");
  const load = useCallback(() => crmApi("/ai-growth/overview").then(setData).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const patch = async (path, id, status) => { await crmApi(`${path}/${id}`, { method: "PATCH", body: { status } }); load(); };
  const addNote = async (e) => {
    e.preventDefault();
    if (!noteUrl.trim() || !noteText.trim()) return;
    await crmApi("/review-miner/notes", { method: "POST", body: { source_url: noteUrl, note: noteText } });
    setNoteUrl(""); setNoteText(""); load();
  };
  if (!data) return <Empty>Loading AI Growth…</Empty>;

  const GrowthList = ({ items, path, testid }) => (
    <div className="space-y-3">
      {items.length === 0 ? <Empty>Nothing here yet</Empty> : items.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center gap-3 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">{r.title}</div>
            <div className="text-xs text-slate-500">{r.detail}</div>
          </div>
          {r.impact && <Pill tone={r.impact === "high" ? "orange" : "slate"}>{r.impact} impact</Pill>}
          <Pill tone={r.status === "approved" || r.status === "done" ? "green" : r.status === "dismissed" ? "red" : "amber"}>{r.status}</Pill>
          <select value={r.status} onChange={(e) => patch(path, r.id, e.target.value)} data-testid={testid}
            className="bg-white/5 border border-white/15 rounded-lg px-2 py-1.5 text-xs outline-none">
            {GROWTH_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="crm-ai-growth">
      <Card>
        <SectionTitle>AEO — answer-engine readiness</SectionTitle>
        <p className="text-xs text-slate-500 mb-4">Content-structure guidance only — no ranking, citation or inclusion promises.</p>
        <GrowthList items={data.aeo} path="/aeo/recommendations" testid="aeo-status" />
      </Card>
      <Card>
        <SectionTitle>Growth autopilot</SectionTitle>
        <p className="text-xs text-slate-500 mb-4">Impact labels are prioritization signals, not revenue projections. Nothing launches without approval.</p>
        <GrowthList items={data.autopilot} path="/growth-autopilot" testid="autopilot-status" />
      </Card>
      <Card>
        <SectionTitle>Review miner</SectionTitle>
        <form onSubmit={addNote} className="flex flex-wrap gap-2.5 mb-5">
          <Input value={noteUrl} onChange={(e) => setNoteUrl(e.target.value)} placeholder="Source URL" className="flex-1 min-w-40" data-testid="review-url" />
          <Input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Observation from the review…" className="flex-[2] min-w-40" data-testid="review-note" />
          <Btn type="submit" data-testid="review-add">Add note</Btn>
        </form>
        {data.review_notes.length === 0 ? <Empty>No review notes mined yet</Empty> : (
          <ul className="space-y-2">
            {data.review_notes.map((n) => (
              <li key={n.id} className="text-sm bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5">
                <span className="text-slate-300">{n.note}</span>
                <a href={n.source_url} target="_blank" rel="noreferrer" className="text-xs text-[#f97316] ml-2 break-all">source</a>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
